import React, { useState, useEffect, useRef } from 'react';
import { expedientesAPI } from '../services/api';
import './ClienteCombobox.css';
import { useToast } from '../contexts/ToastContext';

const ClienteCombobox = ({
  value,
  onChange,
  required = false,
  allowCreate = true,
  /** Si se pasa un array, no se llama al API y se usa esta lista (p. ej. datos ya cargados en el padre). */
  clientesLista = undefined,
}) => {
  const { showSuccess, showError } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredClientes, setFilteredClientes] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientData, setNewClientData] = useState({
    nombre: '',
    dni: '',
    celular: ''
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const [searchingDNI, setSearchingDNI] = useState(false);
  const wrapperRef = useRef(null);

  // Cargar clientes (API) o tomar lista del padre
  useEffect(() => {
    if (Array.isArray(clientesLista)) {
      setClientes(clientesLista);
      return;
    }
    const loadClientes = async () => {
      try {
        const response = await expedientesAPI.getClientes();
        setClientes(response.data);
        setFilteredClientes(response.data);
      } catch (error) {
        console.error('Error al cargar clientes:', error);
      }
    };
    loadClientes();
  }, [clientesLista]);

  // Filtrar clientes basado en el término de búsqueda
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredClientes(clientes);
    } else {
      const filtered = clientes.filter(cliente =>
        cliente.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cliente.dni.includes(searchTerm) ||
        (cliente.celular || '').includes(searchTerm)
      );
      setFilteredClientes(filtered);
    }
  }, [searchTerm, clientes]);

  // Cerrar dropdown cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowNewClientForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Obtener el cliente seleccionado para mostrar en el input
  const selectedCliente = clientes.find((c) => String(c.id) === String(value ?? ''));

  // Actualizar searchTerm cuando se selecciona un cliente (o se limpia la selección)
  useEffect(() => {
    if (selectedCliente) {
      setSearchTerm(selectedCliente.nombre);
    } else if (value === '' || value === null || value === undefined) {
      setSearchTerm('');
    }
  }, [selectedCliente, value]);

  const handleSelectCliente = (cliente) => {
    // Client selected
    onChange({ target: { name: 'clienteId', value: cliente.id.toString() } });
    setSearchTerm(cliente.nombre);
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
    setShowNewClientForm(false);
  };

  // Función para buscar DNI en API MIGO
  const buscarDNI = async (dni) => {
    if (dni.length !== 8 || !/^\d{8}$/.test(dni)) {
      return;
    }

    setSearchingDNI(true);
    try {
      console.log(`[CLIENTE COMBOBOX] Buscando DNI: ${dni}`);
      const response = await expedientesAPI.buscarDNI(dni);
      
      if (response.data.success) {
        const datos = response.data.data;
        console.log(`[CLIENTE COMBOBOX] DNI encontrado:`, datos);
        
        // Autorrellenar el nombre
        setNewClientData(prev => ({
          ...prev,
          nombre: datos.nombreCompleto || `${datos.nombres || ''} ${datos.apellidoPaterno || ''} ${datos.apellidoMaterno || ''}`.trim()
        }));
        
        showSuccess(`✓ Datos encontrados: ${datos.nombreCompleto}`);
      } else {
        showError('DNI no encontrado en RENIEC');
      }
    } catch (error) {
      console.error('[CLIENTE COMBOBOX] Error al buscar DNI:', error);
      if (error.response && error.response.status === 404) {
        showError('DNI no encontrado en RENIEC');
      } else {
        showError('Error al consultar DNI');
      }
    } finally {
      setSearchingDNI(false);
    }
  };

  // Efecto para buscar DNI automáticamente cuando se completan 8 dígitos
  useEffect(() => {
    if (newClientData.dni.length === 8 && /^\d{8}$/.test(newClientData.dni)) {
      buscarDNI(newClientData.dni);
    }
  }, [newClientData.dni]);

  const handleCreateNewClient = async () => {
    setCreatingClient(true);

    try {
      // Validaciones
      if (!newClientData.nombre || !newClientData.dni) {
        throw new Error('Nombre y DNI son obligatorios');
      }

      if (newClientData.dni.length !== 8) {
        throw new Error('El DNI debe tener 8 dígitos');
      }

      const celularLimpio = (newClientData.celular || '').trim();
      if (celularLimpio && celularLimpio.length !== 9) {
        throw new Error('El celular debe tener 9 dígitos');
      }

       // Verificar si el DNI ya existe
       const existingClient = clientes.find(c => c.dni === newClientData.dni);
       if (existingClient) {
         throw new Error(`El DNI ${newClientData.dni} ya existe en la base de datos. Por favor, use un DNI diferente.`);
       }

       // Crear nuevo cliente
       const payload = {
         nombre: newClientData.nombre.trim(),
         dni: newClientData.dni.trim(),
         celular: celularLimpio || null
       };
       const response = await expedientesAPI.createCliente(payload);
      const newCliente = response.data;
      // New client created

      // Recargar la lista completa de clientes para asegurar sincronización
      const clientesResponse = await expedientesAPI.getClientes();
              // Client list updated
      setClientes(clientesResponse.data);
      setFilteredClientes(clientesResponse.data);
      
      // Seleccionar el nuevo cliente
      handleSelectCliente(newCliente);
      
      // Limpiar formulario
      setNewClientData({
        nombre: '',
        dni: '',
        celular: '',
        direccion: ''
      });
      setShowNewClientForm(false);
      
      // Mostrar mensaje de éxito
      showSuccess(`¡Cliente "${newCliente.nombre}" creado exitosamente!`);
         } catch (error) {
       let errorMessage = 'Error al crear cliente';
       
       if (error.response && error.response.data) {
         // Error del servidor
         if (error.response.data.error && error.response.data.error.includes('dni_key')) {
           errorMessage = 'El DNI ya existe en la base de datos. Por favor, use un DNI diferente.';
         } else if (error.response.data.message) {
           errorMessage = error.response.data.message;
         }
       } else if (error.message) {
         // Error de validación del frontend
         errorMessage = error.message;
       }
       
       showError(errorMessage);
     } finally {
      setCreatingClient(false);
    }
  };

  return (
    <div className="cliente-combobox" ref={wrapperRef}>
      <div className="input-group">
        <input
          type="text"
          className="form-control"
          placeholder="Buscar cliente por nombre, DNI o celular..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          required={required}
        />
                 {allowCreate && (
           <button
             type="button"
             className="btn btn-outline-secondary"
             onClick={() => {
               setShowNewClientForm(!showNewClientForm);
               setIsOpen(false);
             }}
             title="Crear nuevo cliente"
           >
             +
           </button>
         )}
      </div>

      {/* Dropdown de clientes existentes */}
      {isOpen && !showNewClientForm && (
        <div className="dropdown-menu show" style={{ width: '100%', maxHeight: '200px', overflowY: 'auto' }}>
          {filteredClientes.length > 0 ? (
            filteredClientes.map(cliente => (
              <button
                key={cliente.id}
                type="button"
                className="dropdown-item"
                onClick={() => handleSelectCliente(cliente)}
              >
                <div>
                  <strong>{cliente.nombre}</strong>
                  <br />
                  <small>DNI: {cliente.dni}{cliente.celular ? ` | Cel: ${cliente.celular}` : ''}</small>
                </div>
              </button>
            ))
          ) : (
            <div className="dropdown-item text-muted">
              No se encontraron clientes
            </div>
          )}
        </div>
      )}

             {/* Formulario para crear nuevo cliente */}
       {showNewClientForm && (
         <div className="card mt-2">
           <div className="card-header">
             <h6 className="mb-0">Crear Nuevo Cliente</h6>
           </div>
           <div className="card-body">
             <div onSubmit={handleCreateNewClient}>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="newNombre" className="form-label">Nombre *</label>
                    <input
                      type="text"
                      id="newNombre"
                      className="form-control"
                      value={newClientData.nombre}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, nombre: e.target.value }))}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="newDni" className="form-label">
                      DNI * 
                      {searchingDNI && <span className="text-muted ms-2">🔍 Buscando...</span>}
                    </label>
                    <input
                      type="text"
                      id="newDni"
                      className="form-control"
                      value={newClientData.dni}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, dni: e.target.value }))}
                      maxLength="8"
                      pattern="[0-9]{8}"
                      required
                      placeholder="Ingresa 8 dígitos"
                    />
                    <small className="form-text text-muted">
                      Se autocompletará el nombre al ingresar DNI válido
                    </small>
                  </div>
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="newCelular" className="form-label">Celular</label>
                    <input
                      type="text"
                      id="newCelular"
                      className="form-control"
                      value={newClientData.celular}
                      onChange={(e) => setNewClientData(prev => ({ ...prev, celular: e.target.value }))}
                      maxLength="9"
                      pattern="[0-9]{9}"
                      placeholder="Opcional (9 dígitos)"
                    />
                  </div>
                </div>
              </div>

                             <div className="d-flex gap-2 mt-3">
                 <button
                   type="button"
                   className="btn btn-primary btn-sm"
                   onClick={handleCreateNewClient}
                   disabled={creatingClient}
                 >
                   {creatingClient ? 'Creando...' : 'Crear Cliente'}
                 </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setShowNewClientForm(false);
                    setNewClientData({
                      nombre: '',
                      dni: '',
                      celular: ''
                    });
                  }}
                >
                  Cancelar
                </button>
                             </div>
             </div>
           </div>
         </div>
       )}

      {/* Input oculto para el valor real */}
      <input
        type="hidden"
        name="clienteId"
        value={value}
        onChange={onChange}
      />
    </div>
  );
};

export default ClienteCombobox;
